import React, { useState, useEffect, useRef } from "react";

function CustomCursor() {
  const [hidden, setHidden] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  // Store coordinates in refs to prevent React re-renders on mousemove
  const mouseCoords = useRef({ x: 0, y: 0 });
  const dotCoords = useRef({ x: 0, y: 0 });
  const ringCoords = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Detect mobile / touch devices
    const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(mobile);
    if (mobile) return;

    const mMove = (e) => {
      mouseCoords.current.x = e.clientX;
      mouseCoords.current.y = e.clientY;
      setHidden(false);
    };

    const mLeave = () => {
      setHidden(true);
    };

    const addHoverEvents = () => {
      const targets = document.querySelectorAll("a, button, [role='button'], .btn, .tech-icons, .contact-card, .project-card-view, .navbar-brand, .nav-link");
      targets.forEach((target) => {
        target.addEventListener("mouseenter", () => setHovered(true));
        target.addEventListener("mouseleave", () => setHovered(false));
      });
    };

    window.addEventListener("mousemove", mMove);
    document.addEventListener("mouseleave", mLeave);
    
    // Set initial hover listeners and use MutationObserver for dynamically loaded elements
    addHoverEvents();
    const observer = new MutationObserver(addHoverEvents);
    observer.observe(document.body, { childList: true, subtree: true });

    // requestAnimationFrame rendering loop with Linear Interpolation (lerp)
    let animationFrameId;
    const renderLoop = () => {
      const dotLerp = 0.35; // Inner dot follows fast
      const ringLerp = 0.12; // Outer ring follows with lag

      // Smooth dot movement
      dotCoords.current.x += (mouseCoords.current.x - dotCoords.current.x) * dotLerp;
      dotCoords.current.y += (mouseCoords.current.y - dotCoords.current.y) * dotLerp;

      // Smooth outer ring movement
      ringCoords.current.x += (mouseCoords.current.x - ringCoords.current.x) * ringLerp;
      ringCoords.current.y += (mouseCoords.current.y - ringCoords.current.y) * ringLerp;

      // Directly update DOM elements style.transform (bypassing React re-renders)
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dotCoords.current.x}px, ${dotCoords.current.y}px, 0) translate(-50%, -50%)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringCoords.current.x}px, ${ringCoords.current.y}px, 0) translate(-50%, -50%)`;
      }

      animationFrameId = requestAnimationFrame(renderLoop);
    };

    animationFrameId = requestAnimationFrame(renderLoop);

    return () => {
      window.removeEventListener("mousemove", mMove);
      document.removeEventListener("mouseleave", mLeave);
      observer.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  if (isMobile || hidden) return null;

  return (
    <>
      {/* Inner Dot */}
      <div
        ref={dotRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "8px",
          height: "8px",
          backgroundColor: "#c770f0",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 99999,
          boxShadow: "0 0 10px rgba(199, 112, 240, 0.8)",
          transform: "translate3d(-100px, -100px, 0)"
        }}
      />
      {/* Outer Glow Ring */}
      <div
        ref={ringRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: hovered ? "50px" : "32px",
          height: hovered ? "50px" : "32px",
          border: hovered ? "2px solid #be50f4" : "1.5px solid rgba(199, 112, 240, 0.6)",
          backgroundColor: hovered ? "rgba(199, 112, 240, 0.15)" : "transparent",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 99998,
          transition: "width 0.25s ease, height 0.25s ease, background-color 0.25s ease, border 0.25s ease, box-shadow 0.25s ease",
          boxShadow: hovered ? "0 0 25px rgba(199, 112, 240, 0.55)" : "none",
          transform: "translate3d(-100px, -100px, 0)"
        }}
      />
    </>
  );
}

export default CustomCursor;
